import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { CONTRACT_TYPES } from '../../@core/enums/contractTypes';
import { compareEmployeesForDisplay } from './employeeSort';

// Generate months array starting from Jan 2026
const generateMonths = () => {
  const months = [];
  const startYear = 2026;
  const startMonth = 0; // January
  for (let i = 0; i < 12; i++) {
    const date = new Date(startYear, startMonth + i, 1);
    months.push({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      label: `${date.toLocaleString('en', { month: 'short' })} ${date.getFullYear()}`
    });
  }
  return months;
};

// Initial state
const initialState = {
  months: generateMonths(),
  // Data structure: { 'YYYY-MM': { calculations: [], summary: {}, exchangeRate: null, workingDays: null } }
  monthData: {},
  employees: [], // Unique list of employees across all months
  expandedEmployees: {}, // { odName: true/false }
  expandedGroups: { gig: true, pe: true, official: true },
  esvAmount: 1902.34,
  officialSalaries: [], // Official employees with their UAH salaries
  officialSalariesLoading: false,
  // Additionals: { calculationId: [] }
  additionals: {},
  additionalsLoading: {},
  // Project breakdown: { calculationId: [] }
  projectBreakdown: {},
  projectBreakdownLoading: {},
  // Email previews: { calculationId: htmlString }
  emailPreviews: {},
  emailPreviewsLoading: {},
  loading: false,
  calculating: {},  // { 'YYYY-MM': true/false }
  error: null,
  contractTypeFilter: null, // null = all, 2 = Official, 3 = GIG, 4 = PE
  companyFilter: null, // null = all, otherwise company name string
  groupBy: 'none', // 'none' | 'company' | 'contractType'
  searchQuery: '',
  includeInactive: false // "Include Inactive Employees" toggle (fms.includeInactiveEmployees.salaryCalculation)
};

// Async thunks
export const fetchMonthData = createAsyncThunk(
  'salaryCalculation/fetchMonthData',
  async ({ year, month }, { getState, rejectWithValue }) => {
    try {
      // Reads the current "Include Inactive Employees" toggle from state so every
      // caller (initial load, toggle change, recalculate, additionals CRUD, etc.)
      // stays consistent with what the user has selected, without each call site
      // needing to thread the flag through explicitly.
      const { includeInactive } = getState().salaryCalculation;
      const response = await axios.get(`/v1/salary-calculation/${year}/${month}`, {
        params: { includeInactive }
      });
      return { year, month, data: response.data };
    } catch (error) {
      if (error.response?.status === 404) {
        return { year, month, data: { calculations: [], summary: null } };
      }
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch salary calculations'
      );
    }
  }
);

export const fetchAllMonthsData = createAsyncThunk(
  'salaryCalculation/fetchAllMonthsData',
  async (_, { getState, dispatch }) => {
    const { months } = getState().salaryCalculation;
    // Fetch months sequentially in descending order (most recent first) to avoid socket hang up
    const sortedMonths = [...months].sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      return b.month - a.month;
    });
    for (const { year, month } of sortedMonths) {
      await dispatch(fetchMonthData({ year, month }));
    }
    return true;
  }
);

export const fetchEsvSetting = createAsyncThunk(
  'salaryCalculation/fetchEsvSetting',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/salary-calculation/settings/esv');
      return response.data.esvAmount;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch ESV setting'
      );
    }
  }
);

export const updateEsvAmount = createAsyncThunk(
  'salaryCalculation/updateEsvAmount',
  async (amount, { rejectWithValue }) => {
    try {
      const response = await axios.put('/v1/salary-calculation/settings/esv', { amount });
      return response.data.esvAmount;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update ESV setting'
      );
    }
  }
);

export const triggerCalculation = createAsyncThunk(
  'salaryCalculation/triggerCalculation',
  async ({ year, month, exchangeRateFirst, exchangeRateSecond, revertToDraft = true }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post('/v1/salary-calculation/calculate', {
        year,
        month,
        exchangeRateFirst,
        exchangeRateSecond,
        revertToDraft
      });
      // Refetch enriched data (with employee names, etc.) after calculation
      dispatch(fetchMonthData({ year, month }));
      return { year, month, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to calculate salaries'
      );
    }
  }
);

export const recalculateEmployee = createAsyncThunk(
  'salaryCalculation/recalculateEmployee',
  async ({ id, year, month }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`/v1/salary-calculation/${id}/recalculate`);
      // Refetch month data to update the UI
      dispatch(fetchMonthData({ year, month }));
      return { year, month, calculation: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to recalculate'
      );
    }
  }
);

export const updateBonus = createAsyncThunk(
  'salaryCalculation/updateBonus',
  async ({ id, bonusUSD, bonusUAH, year, month }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/v1/salary-calculation/${id}`, {
        bonusUSD,
        bonusUAH
      });
      return { year, month, calculation: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update bonus'
      );
    }
  }
);

export const updateExchangeRate = createAsyncThunk(
  'salaryCalculation/updateExchangeRate',
  async ({ year, month, exchangeRateSecond }, { getState, rejectWithValue }) => {
    // This will trigger recalculation with new exchange rate
    try {
      const { monthData } = getState().salaryCalculation;
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;
      const existingData = monthData[monthKey];

      if (existingData?.calculations?.length > 0) {
        // Recalculate with new exchange rate
        const response = await axios.post('/v1/salary-calculation/calculate', {
          year,
          month,
          exchangeRateFirst: exchangeRateSecond,
          exchangeRateSecond
        });
        return { year, month, exchangeRate: exchangeRateSecond, data: response.data };
      }

      return { year, month, exchangeRate: exchangeRateSecond, data: null };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update exchange rate'
      );
    }
  }
);

export const approveCalculation = createAsyncThunk(
  'salaryCalculation/approveCalculation',
  async ({ id, year, month }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/v1/salary-calculation/${id}/approve`);
      return { year, month, calculation: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to approve calculation'
      );
    }
  }
);

export const revertCalculation = createAsyncThunk(
  'salaryCalculation/revertCalculation',
  async ({ id, year, month }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/v1/salary-calculation/${id}/revert`);
      return { year, month, calculation: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to revert calculation'
      );
    }
  }
);

export const approveAllForMonth = createAsyncThunk(
  'salaryCalculation/approveAllForMonth',
  async ({ year, month }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.put(`/v1/salary-calculation/${year}/${month}/approve-all`);
      // Refetch the month data to get updated statuses
      dispatch(fetchMonthData({ year, month }));
      return { year, month, approvedCount: response.data.approvedCount };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to approve all calculations'
      );
    }
  }
);

export const sendSlipEmail = createAsyncThunk(
  'salaryCalculation/sendSlipEmail',
  async ({ id, year, month }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`/v1/salary-calculation/${id}/send-email`);
      // Refetch the month data to get updated email status
      dispatch(fetchMonthData({ year, month }));
      return { id, year, month, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send email'
      );
    }
  }
);

export const sendAllPendingEmails = createAsyncThunk(
  'salaryCalculation/sendAllPendingEmails',
  async ({ year, month }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`/v1/salary-calculation/${year}/${month}/send-all-emails`);
      // Refetch this month's data to update email statuses
      dispatch(fetchMonthData({ year, month }));
      return { year, month, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send emails'
      );
    }
  }
);

export const fetchEmailPreview = createAsyncThunk(
  'salaryCalculation/fetchEmailPreview',
  async ({ calculationId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/v1/salary-calculation/${calculationId}/email-preview`);
      return { calculationId, html: response.data.html };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch email preview'
      );
    }
  }
);

export const fetchOfficialSalaries = createAsyncThunk(
  'salaryCalculation/fetchOfficialSalaries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/v1/salary-calculation/settings/official-salaries');
      return response.data.employees;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch official salaries'
      );
    }
  }
);

export const updateOfficialSalary = createAsyncThunk(
  'salaryCalculation/updateOfficialSalary',
  async ({ userId, year, month, officialSalaryUAH }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `/v1/salary-calculation/settings/official-salary/${userId}/${year}/${month}`,
        { officialSalaryUAH }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update official salary'
      );
    }
  }
);

export const updateOfficialParts = createAsyncThunk(
  'salaryCalculation/updateOfficialParts',
  async ({ id, year, month, officialSalaryUAH, part1UAH, part2UAH, part3UAH }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/v1/salary-calculation/${id}/official-parts`, {
        officialSalaryUAH,
        part1UAH,
        part2UAH,
        part3UAH
      });
      return { year, month, calculation: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update payment parts'
      );
    }
  }
);

// Project breakdown
export const fetchProjectBreakdown = createAsyncThunk(
  'salaryCalculation/fetchProjectBreakdown',
  async ({ calculationId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/v1/salary-calculation/${calculationId}/projects`);
      return { calculationId, projects: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch project breakdown'
      );
    }
  }
);

// Additionals CRUD
export const fetchAdditionals = createAsyncThunk(
  'salaryCalculation/fetchAdditionals',
  async ({ calculationId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/v1/salary-calculation/${calculationId}/additionals`);
      return { calculationId, additionals: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch additionals'
      );
    }
  }
);

export const addAdditional = createAsyncThunk(
  'salaryCalculation/addAdditional',
  async ({ calculationId, description, amount, currency, year, month }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`/v1/salary-calculation/${calculationId}/additionals`, {
        description,
        amount, // Positive = payment, negative = deduction
        currency
      });
      // Refetch month data to get updated enriched calculation
      if (year && month) {
        dispatch(fetchMonthData({ year, month }));
      }
      return {
        calculationId,
        additional: response.data.additional
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add additional'
      );
    }
  }
);

export const updateAdditional = createAsyncThunk(
  'salaryCalculation/updateAdditional',
  async ({ additionalId, calculationId, description, amount, currency, year, month }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.put(`/v1/salary-calculation/additionals/${additionalId}`, {
        description,
        amount,
        currency
      });
      // Refetch month data to get updated enriched calculation
      if (year && month) {
        dispatch(fetchMonthData({ year, month }));
      }
      return {
        calculationId,
        additional: response.data.additional
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update additional'
      );
    }
  }
);

export const deleteAdditional = createAsyncThunk(
  'salaryCalculation/deleteAdditional',
  async ({ additionalId, calculationId, year, month }, { dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`/v1/salary-calculation/additionals/${additionalId}`);
      // Refetch month data to get updated enriched calculation
      if (year && month) {
        dispatch(fetchMonthData({ year, month }));
      }
      return {
        calculationId,
        additionalId
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete additional'
      );
    }
  }
);

// Slice
const salaryCalculationSlice = createSlice({
  name: 'salaryCalculation',
  initialState,
  reducers: {
    toggleEmployeeExpanded: (state, action) => {
      const odName = action.payload;
      state.expandedEmployees[odName] = !state.expandedEmployees[odName];
    },
    toggleGroupExpanded: (state, action) => {
      const group = action.payload;
      state.expandedGroups[group] = !state.expandedGroups[group];
    },
    setContractTypeFilter: (state, action) => {
      state.contractTypeFilter = action.payload;
    },
    setCompanyFilter: (state, action) => {
      state.companyFilter = action.payload;
    },
    setGroupBy: (state, action) => {
      state.groupBy = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setIncludeInactive: (state, action) => {
      state.includeInactive = action.payload;
    },
    setLocalExchangeRate: (state, action) => {
      const { year, month, rateFirst, rateSecond } = action.payload;
      const monthKey = `${year}-${String(month).padStart(2, '0')}`;
      if (!state.monthData[monthKey]) {
        state.monthData[monthKey] = { calculations: [], summary: null };
      }
      if (rateFirst !== undefined) {
        state.monthData[monthKey].exchangeRateFirst = rateFirst;
      }
      if (rateSecond !== undefined) {
        state.monthData[monthKey].exchangeRateSecond = rateSecond;
      }
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchMonthData
      .addCase(fetchMonthData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMonthData.fulfilled, (state, action) => {
        state.loading = false;
        const { year, month, data } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;

        state.monthData[monthKey] = {
          calculations: data.calculations || [],
          summary: data.summary || null,
          exchangeRateFirst: data.calculations?.[0]?.exchangeRateFirst || null,
          exchangeRateSecond: data.calculations?.[0]?.exchangeRateSecond || null,
          workingDays: data.calculations?.[0]?.workingDays || null
        };

        // Update employees list
        const newEmployees = (data.calculations || []).map(c => ({
          userId: c.userId,
          name: c.employeeName,
          firstName: c.firstName || '',
          lastName: c.lastName || '',
          company: c.company || null,
          contractType: c.contractType,
          contractTypeName: c.contractTypeName,
          terminationDate: c.terminationDate || null,
          userState: c.userState || null
        }));

        // Merge with existing employees (avoid duplicates), refreshing
        // terminationDate/userState from this month's fetch so the notice-period
        // highlight reflects the latest known status rather than whatever was
        // cached the first time this employee was seen.
        const existingById = new Map(state.employees.map(e => [e.userId, e]));
        newEmployees.forEach(emp => {
          const existing = existingById.get(emp.userId);
          if (existing) {
            existing.terminationDate = emp.terminationDate;
            existing.userState = emp.userState;
          } else {
            state.employees.push(emp);
            existingById.set(emp.userId, emp);
          }
        });

        // Active/notice-period employees first (by lastName), inactive employees
        // after (by terminationDate desc) — see spec-inactive-toggle-sort-history.md.
        state.employees.sort(compareEmployeesForDisplay);
      })
      .addCase(fetchMonthData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchAllMonthsData
      .addCase(fetchAllMonthsData.pending, (state) => {
        state.loading = true;
        // A full refetch (initial load, or the "Include Inactive Employees"
        // toggle changing) is about to repopulate every month from scratch —
        // clear the accumulator so an employee who's no longer returned by
        // any month (e.g. the toggle just turned off) doesn't linger forever.
        state.employees = [];
        state.monthData = {};
      })
      .addCase(fetchAllMonthsData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAllMonthsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchEsvSetting
      .addCase(fetchEsvSetting.fulfilled, (state, action) => {
        state.esvAmount = action.payload;
      })
      // updateEsvAmount
      .addCase(updateEsvAmount.fulfilled, (state, action) => {
        state.esvAmount = action.payload;
      })
      // triggerCalculation
      .addCase(triggerCalculation.pending, (state, action) => {
        const { year, month } = action.meta.arg;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        state.calculating[monthKey] = true;
      })
      .addCase(triggerCalculation.fulfilled, (state, action) => {
        const { year, month, data } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        state.calculating[monthKey] = false;

        if (data.calculations) {
          state.monthData[monthKey] = {
            calculations: data.calculations,
            summary: data.summary || null,
            exchangeRateFirst: data.calculations[0]?.exchangeRateFirst || null,
            exchangeRateSecond: data.calculations[0]?.exchangeRateSecond || null,
            workingDays: data.calculations[0]?.workingDays || null
          };
        }
      })
      .addCase(triggerCalculation.rejected, (state, action) => {
        const { year, month } = action.meta.arg;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        state.calculating[monthKey] = false;
        state.error = action.payload;
      })
      // updateBonus
      .addCase(updateBonus.fulfilled, (state, action) => {
        const { year, month, calculation } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const monthCalcs = state.monthData[monthKey]?.calculations || [];
        const index = monthCalcs.findIndex(c => c.id === calculation.id);
        if (index !== -1) {
          state.monthData[monthKey].calculations[index] = {
            ...state.monthData[monthKey].calculations[index],
            ...calculation
          };
        }
      })
      // updateExchangeRate
      .addCase(updateExchangeRate.fulfilled, (state, action) => {
        const { year, month, exchangeRateFirst, exchangeRateSecond, data } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;

        if (!state.monthData[monthKey]) {
          state.monthData[monthKey] = { calculations: [], summary: null };
        }
        if (exchangeRateFirst) state.monthData[monthKey].exchangeRateFirst = exchangeRateFirst;
        if (exchangeRateSecond) state.monthData[monthKey].exchangeRateSecond = exchangeRateSecond;

        if (data?.calculations) {
          state.monthData[monthKey].calculations = data.calculations;
          state.monthData[monthKey].summary = data.summary;
        }
      })
      // approveCalculation
      .addCase(approveCalculation.fulfilled, (state, action) => {
        const { year, month, calculation } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const monthCalcs = state.monthData[monthKey]?.calculations || [];
        const index = monthCalcs.findIndex(c => c.id === calculation.id);
        if (index !== -1) {
          state.monthData[monthKey].calculations[index] = {
            ...state.monthData[monthKey].calculations[index],
            ...calculation
          };
        }
      })
      .addCase(approveCalculation.rejected, (state, action) => {
        state.error = action.payload;
      })
      // revertCalculation
      .addCase(revertCalculation.fulfilled, (state, action) => {
        const { year, month, calculation } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const monthCalcs = state.monthData[monthKey]?.calculations || [];
        const index = monthCalcs.findIndex(c => c.id === calculation.id);
        if (index !== -1) {
          state.monthData[monthKey].calculations[index] = {
            ...state.monthData[monthKey].calculations[index],
            ...calculation
          };
        }
      })
      .addCase(revertCalculation.rejected, (state, action) => {
        state.error = action.payload;
      })
      // approveAllForMonth
      .addCase(approveAllForMonth.rejected, (state, action) => {
        state.error = action.payload;
      })
      // fetchOfficialSalaries
      .addCase(fetchOfficialSalaries.pending, (state) => {
        state.officialSalariesLoading = true;
      })
      .addCase(fetchOfficialSalaries.fulfilled, (state, action) => {
        state.officialSalariesLoading = false;
        state.officialSalaries = action.payload;
      })
      .addCase(fetchOfficialSalaries.rejected, (state, action) => {
        state.officialSalariesLoading = false;
        state.error = action.payload;
      })
      // updateOfficialSalary
      .addCase(updateOfficialSalary.fulfilled, (state, action) => {
        const { userId, year, month, officialSalaryUAH } = action.payload;
        // Update currentOfficialSalaryUAH for the specific month only
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const monthCalcs = state.monthData[monthKey]?.calculations;
        if (monthCalcs) {
          const calcIndex = monthCalcs.findIndex(c => c.userId === userId);
          if (calcIndex !== -1) {
            state.monthData[monthKey].calculations[calcIndex].currentOfficialSalaryUAH = officialSalaryUAH;
          }
        }
      })
      .addCase(updateOfficialSalary.rejected, (state, action) => {
        state.error = action.payload;
      })
      // updateOfficialParts
      .addCase(updateOfficialParts.fulfilled, (state, action) => {
        const { year, month, calculation } = action.payload;
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        const monthCalcs = state.monthData[monthKey]?.calculations || [];
        const index = monthCalcs.findIndex(c => c.id === calculation.id);
        if (index !== -1) {
          state.monthData[monthKey].calculations[index] = {
            ...state.monthData[monthKey].calculations[index],
            ...calculation
          };
        }
      })
      .addCase(updateOfficialParts.rejected, (state, action) => {
        state.error = action.payload;
      })
      // fetchProjectBreakdown
      .addCase(fetchProjectBreakdown.pending, (state, action) => {
        const { calculationId } = action.meta.arg;
        state.projectBreakdownLoading[calculationId] = true;
      })
      .addCase(fetchProjectBreakdown.fulfilled, (state, action) => {
        const { calculationId, projects } = action.payload;
        state.projectBreakdown[calculationId] = projects;
        state.projectBreakdownLoading[calculationId] = false;
      })
      .addCase(fetchProjectBreakdown.rejected, (state, action) => {
        const { calculationId } = action.meta.arg;
        state.projectBreakdownLoading[calculationId] = false;
        state.error = action.payload;
      })
      // fetchAdditionals
      .addCase(fetchAdditionals.pending, (state, action) => {
        const { calculationId } = action.meta.arg;
        state.additionalsLoading[calculationId] = true;
      })
      .addCase(fetchAdditionals.fulfilled, (state, action) => {
        const { calculationId, additionals } = action.payload;
        state.additionals[calculationId] = additionals;
        state.additionalsLoading[calculationId] = false;
      })
      .addCase(fetchAdditionals.rejected, (state, action) => {
        const { calculationId } = action.meta.arg;
        state.additionalsLoading[calculationId] = false;
        state.error = action.payload;
      })
      // addAdditional
      .addCase(addAdditional.fulfilled, (state, action) => {
        const { calculationId, additional } = action.payload;
        if (!state.additionals[calculationId]) {
          state.additionals[calculationId] = [];
        }
        state.additionals[calculationId].push(additional);
        // Month data will be refreshed by fetchMonthData dispatch
      })
      .addCase(addAdditional.rejected, (state, action) => {
        state.error = action.payload;
      })
      // updateAdditional
      .addCase(updateAdditional.fulfilled, (state, action) => {
        const { calculationId, additional } = action.payload;
        const list = state.additionals[calculationId] || [];
        const index = list.findIndex(a => a.id === additional.id);
        if (index !== -1) {
          state.additionals[calculationId][index] = additional;
        }
        // Month data will be refreshed by fetchMonthData dispatch
      })
      .addCase(updateAdditional.rejected, (state, action) => {
        state.error = action.payload;
      })
      // deleteAdditional
      .addCase(deleteAdditional.fulfilled, (state, action) => {
        const { calculationId, additionalId } = action.payload;
        const list = state.additionals[calculationId] || [];
        state.additionals[calculationId] = list.filter(a => a.id !== additionalId);
        // Month data will be refreshed by fetchMonthData dispatch
      })
      .addCase(deleteAdditional.rejected, (state, action) => {
        state.error = action.payload;
      })
      // fetchEmailPreview
      .addCase(fetchEmailPreview.pending, (state, action) => {
        const { calculationId } = action.meta.arg;
        state.emailPreviewsLoading[calculationId] = true;
      })
      .addCase(fetchEmailPreview.fulfilled, (state, action) => {
        const { calculationId, html } = action.payload;
        state.emailPreviews[calculationId] = html;
        state.emailPreviewsLoading[calculationId] = false;
      })
      .addCase(fetchEmailPreview.rejected, (state, action) => {
        const { calculationId } = action.meta.arg;
        state.emailPreviewsLoading[calculationId] = false;
        state.error = action.payload;
      });
  }
});

// Actions
export const {
  toggleEmployeeExpanded,
  toggleGroupExpanded,
  setContractTypeFilter,
  setCompanyFilter,
  setGroupBy,
  setSearchQuery,
  setIncludeInactive,
  setLocalExchangeRate,
  clearError
} = salaryCalculationSlice.actions;

// Selectors
export const selectMonths = (state) => state.salaryCalculation.months;
export const selectMonthData = (state) => state.salaryCalculation.monthData;
export const selectOfficialSalaries = (state) => state.salaryCalculation.officialSalaries;
export const selectOfficialSalariesLoading = (state) => state.salaryCalculation.officialSalariesLoading;
export const selectEmployees = (state) => state.salaryCalculation.employees;
export const selectExpandedEmployees = (state) => state.salaryCalculation.expandedEmployees;
export const selectExpandedGroups = (state) => state.salaryCalculation.expandedGroups;
export const selectEsvAmount = (state) => state.salaryCalculation.esvAmount;
export const selectLoading = (state) => state.salaryCalculation.loading;
export const selectCalculating = (state) => state.salaryCalculation.calculating;
export const selectError = (state) => state.salaryCalculation.error;
export const selectContractTypeFilter = (state) => state.salaryCalculation.contractTypeFilter;
export const selectCompanyFilter = (state) => state.salaryCalculation.companyFilter;
export const selectGroupBy = (state) => state.salaryCalculation.groupBy;
export const selectSearchQuery = (state) => state.salaryCalculation.searchQuery;
export const selectIncludeInactive = (state) => state.salaryCalculation.includeInactive;
export const selectAdditionals = (state) => state.salaryCalculation.additionals;
export const selectAdditionalsLoading = (state) => state.salaryCalculation.additionalsLoading;
export const selectProjectBreakdown = (state) => state.salaryCalculation.projectBreakdown;
export const selectProjectBreakdownLoading = (state) => state.salaryCalculation.projectBreakdownLoading;
export const selectEmailPreviews = (state) => state.salaryCalculation.emailPreviews;
export const selectEmailPreviewsLoading = (state) => state.salaryCalculation.emailPreviewsLoading;
export const selectHasAnyData = (state) => Object.keys(state.salaryCalculation.monthData).length > 0;

// Derived selectors
export const selectFilteredEmployees = (state) => {
  const { employees, contractTypeFilter, companyFilter, searchQuery } = state.salaryCalculation;

  return employees.filter(emp => {
    if (contractTypeFilter && emp.contractType !== contractTypeFilter) {
      return false;
    }
    if (companyFilter && emp.company !== companyFilter) {
      return false;
    }
    if (searchQuery && !emp.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });
};

// Distinct companies present across loaded employees, for the company filter dropdown.
export const selectCompanyOptions = (state) => {
  const companySet = new Set(state.salaryCalculation.employees.map(e => e.company).filter(Boolean));
  const companies = [...companySet].sort();
  return [{ value: null, label: 'All Companies' }, ...companies.map(c => ({ value: c, label: c }))];
};

export const selectEmployeesByType = (state) => {
  const filtered = selectFilteredEmployees(state);
  return {
    gig: filtered.filter(e => e.contractType === CONTRACT_TYPES.GIG),
    pe: filtered.filter(e => e.contractType === CONTRACT_TYPES.PE),
    cash: filtered.filter(e => e.contractType === CONTRACT_TYPES.CASH),
    official: filtered.filter(e => e.contractType === CONTRACT_TYPES.OFFICIAL)
  };
};

export const selectPendingEmailsByMonth = (state) => {
  const { monthData } = state.salaryCalculation;
  const counts = {};
  for (const key of Object.keys(monthData)) {
    const calcs = monthData[key]?.calculations || [];
    counts[key] = calcs.filter(c => c.status === 'approved' && !c.emailSentAt).length;
  }
  return counts;
};

export const selectTotalsByMonth = (state) => {
  const { monthData, months } = state.salaryCalculation;
  const totals = {};

  months.forEach(({ year, month }) => {
    const monthKey = `${year}-${String(month).padStart(2, '0')}`;
    const data = monthData[monthKey];

    if (data?.summary) {
      totals[monthKey] = {
        gig: data.summary.byContractType?.gig || 0,
        pe: data.summary.byContractType?.pe || 0,
        official: data.summary.byContractType?.official || 0,
        total: data.summary.totalNetUAH || 0,
        totalGrossUAH: data.summary.totalGrossUAH || 0
      };
    }
  });

  return totals;
};

export default salaryCalculationSlice.reducer;
